import React, { FC } from "react";
import { Fade } from "react-awesome-reveal";
import classNames from "classnames";
import loadable from "@loadable/component";
import { DEFAULT_FADE_DURATION } from "@app/constants";
import { Card, RichText } from "../../models";
import {
  BlockHeader,
  HeaderLevel,
} from "../../../common/block-header/BlockHeader";
import { BlockBody } from "../../../common/block-body/BlockBody";
import { Size } from "../../enums";

const ReactPlayer = loadable(() => import("react-player"));

type Props = {
  title: RichText;
  cards: Card[];
};

export const Cards: FC<Props> = ({ title, cards }) => {
  const isLargeCards = cards.some((card) => card.size === Size.LARGE);

  return (
    <div
      className={classNames(
        "mt-20 flex flex-col items-center px-5",
        "g:px-16 lg:mt-25",
        "xl:mt-40 xl:px-24",
      )}
    >
      <Fade duration={DEFAULT_FADE_DURATION} triggerOnce>
        <BlockHeader
          richText={title}
          className={classNames(
            "mb-10 text-medium text-primary-content",
            "lg:text-large",
            "xl:mb-25 xl:text-xx-large",
          )}
        />
      </Fade>
      <div
        className={classNames(
          "mx-auto w-full max-w-container",
          "flex flex-col gap-y-20",
          "md:grid md:grid-cols-12 md:gap-10",
          "xxl:gap-x-12 xxl:gap-y-15",
        )}
      >
        {cards.map((card) => (
          <div
            className={classNames("md:col-span-6", {
              "xxl:col-span-7 xxl:row-span-2": card.size === Size.LARGE,
              "xxl:col-span-5 xxl:aspect-[16/10]": card.size !== Size.LARGE,
              "xxl:col-span-6": card.size !== Size.LARGE && !isLargeCards,
            })}
            key={card._uid}
          >
            <Fade
              duration={DEFAULT_FADE_DURATION}
              triggerOnce
              className="h-full w-full"
            >
              <div
                className={classNames(
                  "relative h-full w-full p-5 pb-2.5",
                  "rounded-lg border border-divider",
                  "lg:px-9 lg:py-11",
                )}
              >
                <BlockHeader
                  richText={card.title}
                  headerLevel={HeaderLevel.H4}
                  className={classNames(
                    "relative text-primary-content",
                    "mb-2.5 text-medium",
                    "lg:mb-5",
                    "xl:z-20 xl:text-large",
                    card.size === Size.LARGE
                      ? "xxl:max-w-[60%]"
                      : "xxl:max-w-[65%]",
                  )}
                />
                <div
                  className={classNames(
                    "relative text-base-400",
                    "text-regular",
                    "xl:z-20 xl:text-medium",
                    "xxl:max-w-[55%]",
                  )}
                >
                  <BlockBody richText={card.text} />
                </div>
                <div
                  className={classNames(
                    "relative",
                    "xxl:absolute xxl:right-5 xxl:z-10",
                    card.size === Size.LARGE
                      ? "xxl:bottom-5 xxl:w-3/4"
                      : "xxl:bottom-10 xxl:w-2/5",
                  )}
                >
                  <ReactPlayer
                    url={card.media.filename}
                    width="100%"
                    height="auto"
                    playing
                    muted
                    loop
                    playsinline
                    config={{
                      file: {
                        attributes: { disablePictureInPicture: true },
                      },
                    }}
                  />
                </div>
              </div>
            </Fade>
          </div>
        ))}
      </div>
    </div>
  );
};
